import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';

import {
  AI_API_URL,
  ASSET_API_URL,
  ASSET_BY_ID_QUERY_KEY,
  GET_EVENT_ASSETS,
  GET_ORGANIZATION_ASSETS
} from '@/constants';
import { AssetScope } from '@/enums';
import { apiUrl, getQueryParams } from '@/helpers';
import {
  AssetClonePayload,
  CreateEditAssetPayload,
  ErrorResponse,
  GenerateSpeechTextPayload,
  GenerateSpeechTextResponse,
  Options,
  SasTokenPayload,
  UpdateAssetBlurredUrlPayload,
  UpdateAssetNamePayload,
  UpdateAssetPayload
} from '@/types';

export const assetEndpoints = {
  getEventAssets: (
    eventId: string,
    tagIds: string[],
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    assetTypes?: string[],
    assetScope?: AssetScope
  ) =>
    axios.get(
      `${apiUrl()}${ASSET_API_URL}/event/${eventId}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.AssetType': assetTypes as string[],
        'filter.AssetScope': assetScope as AssetScope,
        'filter.Tags[0].Id': tagIds
      })}`
    ),
  createAsset: (payload: CreateEditAssetPayload) =>
    axios.post(`${apiUrl()}${ASSET_API_URL}`, payload),
  createSasToken: (payload: SasTokenPayload) =>
    axios.post(`${apiUrl()}${ASSET_API_URL}/create_sas_token`, payload),
  getAsset: (id: number) => axios.get(`${apiUrl()}${ASSET_API_URL}/${id}`),
  deleteAsset: (id: number) => axios.delete(`${apiUrl()}${ASSET_API_URL}/${id}`),
  getPublicAssets: (
    tagIds: string[],
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    assetTypes?: string[],
    sortingField?: string,
    sortingAsc?: boolean
  ) =>
    axios.get(
      `${apiUrl()}${ASSET_API_URL}/public${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.AssetType': assetTypes as string[],
        'filter.Tags[0].Id': tagIds,
        'sorting.Field': sortingField as string,
        'sorting.Asc': sortingAsc as boolean
      })}`
    ),
  updateAssetTags: (payload: UpdateAssetPayload[]) =>
    axios.put(`${apiUrl()}${ASSET_API_URL}`, payload),
  updateAsset: (payload: CreateEditAssetPayload) =>
    axios.put(`${apiUrl()}${ASSET_API_URL}/update_asset`, payload),
  getOrganizationAssets: (
    pageSize: number,
    cursor: number,
    organizationId?: number,
    searchQuery?: string,
    assetTypes?: string[],
    sortingField?: string,
    sortingAsc?: boolean
  ) =>
    axios.get(
      `${apiUrl()}${ASSET_API_URL}/organization${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'search.Text': searchQuery as string,
        'filter.OrganizationId': organizationId as number,
        'filter.AssetType': assetTypes as string[],
        'sorting.Field': sortingField as string,
        'sorting.Asc': sortingAsc as boolean
      })}`
    ),
  updateAssetName: (payload: UpdateAssetNamePayload) =>
    axios.patch(`${apiUrl()}${ASSET_API_URL}/update_asset_name`, payload),
  generateSpeechText: (payload: GenerateSpeechTextPayload) =>
    axios.post(`${apiUrl()}${AI_API_URL}/generateSpeechText`, payload),
  estimateReadingTime: (text: string) =>
    axios.post(`${apiUrl()}${AI_API_URL}/estimatereadingtime`, {
      text
    }),
  createCloneAsset: (payload: AssetClonePayload) =>
    axios.post(`${apiUrl()}${ASSET_API_URL}/clone`, payload),
  updateAssetBlurredUrl: (payload: UpdateAssetBlurredUrlPayload) =>
    axios.patch(`${apiUrl()}${ASSET_API_URL}/update_blurred_image_url`, payload)
};

export const useGetEventAssets = (
  eventId: string,
  tagIds: string[],
  pageSize: number,
  searchQuery?: string,
  assetTypes?: string[],
  assetScope?: AssetScope,
  keepPreviousData?: boolean,
  enabled?: boolean
) => {
  return useInfiniteQuery(
    [GET_EVENT_ASSETS, { eventId, tagIds, pageSize, searchQuery, assetTypes, assetScope }],
    ({ pageParam }) =>
      assetEndpoints.getEventAssets(
        eventId,
        tagIds,
        pageSize,
        pageParam,
        searchQuery,
        assetTypes,
        assetScope
      ),
    {
      keepPreviousData: keepPreviousData ?? true,
      enabled: !!eventId && enabled,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage && firstPage.data?.previousCursor !== undefined) {
          return firstPage.data?.previousCursor;
        }
        return undefined;
      }
    }
  );
};

export const useCreateAssetMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse<CreateEditAssetPayload>, ErrorResponse, CreateEditAssetPayload>(
    assetEndpoints.createAsset,
    {
      onSuccess: (response) => {
        options.onSuccess(response);
        queryClient.invalidateQueries([GET_EVENT_ASSETS]);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useCreateSasTokenMutation = (queryClient: QueryClient, options: Options) => {
  const mutation = useMutation(assetEndpoints.createSasToken, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_EVENT_ASSETS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
  return mutation;
};

export const useGetAssetQuery = (id: number, options?: Options) => {
  return useQuery([ASSET_BY_ID_QUERY_KEY, { id }], () => assetEndpoints.getAsset(id), {
    enabled: !!id,
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteAssetMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(assetEndpoints.deleteAsset, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_EVENT_ASSETS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useDeleteAssetsMutation = (queryClient: QueryClient) => {
  return useMutation(
    async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map((id) => assetEndpoints.deleteAsset(id)));
      return results.filter((result) => result.status === 'rejected').length;
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries([GET_EVENT_ASSETS]);
      }
    }
  );
};

export const useGetPublicAssets = (
  tagIds: string[],
  pageSize: number,
  enabled?: boolean,
  searchQuery?: string,
  assetTypes?: string[],
  sortingField?: string,
  sortingAsc?: boolean
) => {
  return useInfiniteQuery(
    [GET_EVENT_ASSETS, { tagIds, pageSize, searchQuery, assetTypes, sortingField, sortingAsc }],
    ({ pageParam }) =>
      assetEndpoints.getPublicAssets(
        tagIds,
        pageSize,
        pageParam,
        searchQuery,
        assetTypes,
        sortingField,
        sortingAsc
      ),
    {
      enabled,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useGetOrganizationAssets = (
  pageSize: number,
  enabled?: boolean,
  organizationId?: number,
  searchQuery?: string,
  assetTypes?: string[],
  sortingField?: string,
  sortingAsc?: boolean,
  userId?: number
) => {
  return useInfiniteQuery(
    [
      GET_ORGANIZATION_ASSETS,
      { pageSize, organizationId, searchQuery, assetTypes, sortingField, sortingAsc, userId }
    ],
    ({ pageParam }) =>
      assetEndpoints.getOrganizationAssets(
        pageSize,
        pageParam,
        organizationId,
        searchQuery,
        assetTypes,
        sortingField,
        sortingAsc
      ),
    {
      enabled,
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useUpdateAssetTags = (queryClient: QueryClient, options: Options) => {
  return useMutation(assetEndpoints.updateAssetTags, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_EVENT_ASSETS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateAsset = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse<CreateEditAssetPayload>, ErrorResponse, CreateEditAssetPayload>(
    assetEndpoints.updateAsset,
    {
      onSuccess: () => {
        options.onSuccess();
        queryClient.invalidateQueries([ASSET_BY_ID_QUERY_KEY]);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useUpdateAssetName = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse, ErrorResponse, UpdateAssetNamePayload>(
    assetEndpoints.updateAssetName,
    {
      onSuccess: (response) => {
        options.onSuccess(response);
        queryClient.invalidateQueries([GET_EVENT_ASSETS]);
        queryClient.refetchQueries([GET_EVENT_ASSETS]);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useUpdateAssetBlurredUrl = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse, ErrorResponse, UpdateAssetBlurredUrlPayload>(
    assetEndpoints.updateAssetBlurredUrl,
    {
      onSuccess: (response) => {
        options.onSuccess(response);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGenerateSpeechText = (options: Options) => {
  return useMutation<
    AxiosResponse<GenerateSpeechTextResponse>,
    ErrorResponse,
    GenerateSpeechTextPayload
  >(assetEndpoints.generateSpeechText, {
    onSuccess: (response) => {
      options.onSuccess(response);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useEstimateReadingTime = (options?: Options) => {
  return useMutation<AxiosResponse<number>, ErrorResponse, string>(
    assetEndpoints.estimateReadingTime,
    {
      onSuccess: (response) => {
        options?.onSuccess(response);
      },
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useCreateAssetCloneMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation<AxiosResponse<CreateEditAssetPayload>, ErrorResponse, AssetClonePayload>(
    assetEndpoints.createCloneAsset,
    {
      onSuccess: (response) => {
        options.onSuccess(response);
        queryClient.invalidateQueries([GET_EVENT_ASSETS]);
      },
      onError: (error) => {
        options.onError(error as ErrorResponse);
      }
    }
  );
};
